/*
 * vega-visemes — real-time audio -> viseme engine
 * Zero dependencies. Pure arithmetic. No DOM, no Node APIs.
 * This file is the reference spec for the Swift / Kotlin ports.
 *
 * Usage (streaming):
 *   const e = new VisemeEngine(config);
 *   for (const chunk of pcmChunks) for (const ev of e.push(chunk)) render(ev);
 *   for (const ev of e.flush()) render(ev);
 *
 * push() takes Float32Array in [-1,1] at config.audio.sampleRate.
 * It returns zero or more events: { t, viseme, intensity, level, f1, f2, db }
 *   t  = seconds from stream start, of the *audio*, not of wall-clock.
 * Call push() when audio is ENQUEUED for playback, not when it is heard —
 * that is what makes the lookahead free.
 */

// ---------------------------------------------------------------- FFT
function makeFFT(n) {
  const levels = Math.log2(n) | 0;
  const cos = new Float32Array(n / 2), sin = new Float32Array(n / 2);
  for (let i = 0; i < n / 2; i++) {
    cos[i] = Math.cos(2 * Math.PI * i / n);
    sin[i] = Math.sin(2 * Math.PI * i / n);
  }
  return function (re, im) {
    for (let i = 0; i < n; i++) {
      let j = 0;
      for (let b = 0; b < levels; b++) j = (j << 1) | ((i >> b) & 1);
      if (j > i) { let t = re[i]; re[i] = re[j]; re[j] = t; t = im[i]; im[i] = im[j]; im[j] = t; }
    }
    for (let size = 2; size <= n; size <<= 1) {
      const half = size >> 1, step = n / size;
      for (let i = 0; i < n; i += size)
        for (let j = i, k = 0; j < i + half; j++, k += step) {
          const tre = re[j + half] * cos[k] + im[j + half] * sin[k];
          const tim = -re[j + half] * sin[k] + im[j + half] * cos[k];
          re[j + half] = re[j] - tre; im[j + half] = im[j] - tim;
          re[j] += tre; im[j] += tim;
        }
    }
  };
}

// ------------------------------------------------- LPC (Levinson-Durbin)
/* Scratch buffers: this runs 100x/sec/avatar and used to allocate the
   coefficient array plus a .slice() on every order iteration (23 allocations
   per frame). Mobile GC notices. */
const _lev = { a: null, prev: null, order: -1 };
function levinson(r, order) {
  if (!(r[0] > 0)) return null;
  if (_lev.order !== order) {
    _lev.a = new Float64Array(order + 1);
    _lev.prev = new Float64Array(order + 1);
    _lev.order = order;
  }
  const a = _lev.a, prev = _lev.prev;
  a.fill(0); a[0] = 1;
  let e = r[0];
  for (let i = 1; i <= order; i++) {
    let acc = r[i];
    for (let j = 1; j < i; j++) acc += a[j] * r[i - j];
    const k = -acc / e;
    prev.set(a);
    for (let j = 1; j < i; j++) a[j] = prev[j] + k * prev[i - j];
    a[i] = k;
    e *= (1 - k * k);
    if (!(e > 0)) return null;
  }
  return a;
}

/* Formants by peak-picking the LPC envelope.
   Deliberately avoids polynomial root-finding: this is real-arithmetic only,
   so it ports to Swift/Kotlin without a complex-number library. */
const _env = { mag: null };
function formantsFromLPC(a, order, sr, cfg) {
  const { formantMinHz: lo, formantMaxHz: hi, envelopeBins: N } = cfg;
  if (!_env.mag || _env.mag.length !== N) _env.mag = new Float64Array(N);
  const mag = _env.mag;
  for (let b = 0; b < N; b++) {
    const w = 2 * Math.PI * (lo + (hi - lo) * b / (N - 1)) / sr;
    let re = 0, im = 0;
    for (let k = 0; k <= order; k++) { re += a[k] * Math.cos(w * k); im -= a[k] * Math.sin(w * k); }
    mag[b] = 1 / Math.sqrt(re * re + im * im + 1e-12);
  }
  const peaks = [];
  for (let b = 1; b < N - 1; b++)
    if (mag[b] > mag[b - 1] && mag[b] >= mag[b + 1])
      peaks.push({ hz: lo + (hi - lo) * b / (N - 1), m: mag[b] });
  peaks.sort((x, y) => x.hz - y.hz);
  return peaks;
}

/* Framing + feature extraction, shared by the DSP classifier and the aligner
   so both see byte-identical acoustics. */
export class FrameAnalyser {
  constructor(config){
    this.cfg=config; const {frameSamples}=config.audio; this.N=frameSamples;
    this.fft=makeFFT(frameSamples);
    this.win=new Float32Array(frameSamples);
    for(let i=0;i<frameSamples;i++) this.win[i]=0.54-0.46*Math.cos(2*Math.PI*i/(frameSamples-1));
    this.re=new Float32Array(frameSamples); this.im=new Float32Array(frameSamples);
    this.reset();
  }
  reset(){ this.buf=new Float32Array(0); this.samplesConsumed=0; this.prevDb=-120; }
  push(samples){
    const {hopSamples}=this.cfg.audio, N=this.N, out=[];
    const m=new Float32Array(this.buf.length+samples.length);
    m.set(this.buf); m.set(samples,this.buf.length);
    let off=0;
    while(off+N<=m.length){
      const t=(this.samplesConsumed+off)/this.cfg.audio.sampleRate;
      const f=analyseFrame(this,m.subarray(off,off+N),t);
      f.dDb=f.db-this.prevDb; this.prevDb=f.db;
      out.push(f); off+=hopSamples;
    }
    this.samplesConsumed+=off; this.buf=m.slice(off);
    return out;
  }
}

// ------------------------------------------- shared frame feature extraction
function analyseFrame(self, frame, t) {
    const cfg = self.cfg, N = self.N, sr = cfg.audio.sampleRate;

    let sum = 0, zc = 0;
    for (let i = 0; i < N; i++) {
      sum += frame[i] * frame[i];
      if (i && ((frame[i] >= 0) !== (frame[i - 1] >= 0))) zc++;
    }
    const rms = Math.sqrt(sum / N);
    const db = 20 * Math.log10(rms + 1e-12);
    const zcr = zc / (N - 1);

    // windowed spectrum -> centroid + spectral tilt
    for (let i = 0; i < N; i++) { self.re[i] = frame[i] * self.win[i]; self.im[i] = 0; }
    self.fft(self.re, self.im);
    let num = 0, den = 0, loE = 0, hiE = 0;
    for (let b = 1; b < N / 2; b++) {
      const hz = b * sr / N;
      const p = self.re[b] * self.re[b] + self.im[b] * self.im[b];
      num += hz * p; den += p;
      if (hz < 1000) loE += p; else if (hz > 3000) hiE += p;
    }
    const centroid = den > 0 ? num / den : 0;
    const tiltDb = 10 * Math.log10((hiE + 1e-12) / (loE + 1e-12));

    // pre-emphasised autocorrelation -> LPC -> formants
    const order = cfg.analysis.lpcOrder;
    if (!self._pe || self._pe.length !== N) { self._pe = new Float64Array(N); self._r = new Float64Array(order + 1); }
    const pe = self._pe;
    pe[0] = frame[0];
    for (let i = 1; i < N; i++) pe[i] = frame[i] - cfg.analysis.preEmphasis * frame[i - 1];
    for (let i = 0; i < N; i++) pe[i] *= self.win[i];
    const r = self._r;
    for (let k = 0; k <= order; k++) { let s = 0; for (let i = k; i < N; i++) s += pe[i] * pe[i - k]; r[k] = s; }
    let f1 = 0, f2 = 0;
    const a = levinson(r, order);
    if (a) {
      const pk = formantsFromLPC(a, order, sr, cfg.analysis);
      if (pk.length >= 2) { f1 = pk[0].hz; f2 = pk[1].hz; }
      else if (pk.length === 1) { f1 = pk[0].hz; }
    }

    const an = cfg.analysis;
    const silent = db < an.silenceFloorDb;
    const voiced = !silent && zcr <= an.voicedZcrMax && db >= an.voicedMinDb;
    return { t, db, zcr, centroid, tiltDb, f1, f2, silent, voiced };
  }


// ---------------------------------------------------------------- engine
export class VisemeEngine {
  constructor(config) {
    this.cfg = config;
    const { frameSamples } = config.audio;
    this.N = frameSamples;
    this.fft = makeFFT(frameSamples);
    this.win = new Float32Array(frameSamples);
    for (let i = 0; i < frameSamples; i++)
      this.win[i] = 0.54 - 0.46 * Math.cos(2 * Math.PI * i / (frameSamples - 1));
    this.re = new Float32Array(frameSamples);
    this.im = new Float32Array(frameSamples);
    this.vowelKeys = Object.keys(config.vowels).filter(k => !k.startsWith('_'));
    this.reset();
  }

  reset() {
    this.buf = new Float32Array(0);
    this.samplesConsumed = 0;
    this.pending = [];        // lookahead queue of analysed frames
    this.emitted = null;      // last emitted viseme
    this.emittedAt = -1e9;
    this.intensity = 0;
    this.prevSample = 0;
  }

  _analyse(frame,t){ return analyseFrame(this,frame,t); }

  // ---- per-frame classification (no temporal logic here) --------------
  _classify(f) {
    const cfg = this.cfg;
    if (f.silent) return 'sil';

    const C = cfg.consonants;
    for (const key of C.order) {
      const c = C[key];
      if (!c) continue;
      if (c.voiced !== undefined && c.voiced !== f.voiced) continue;
      if (c.minZcr !== undefined && f.zcr < c.minZcr) continue;
      if (c.minCentroidHz !== undefined && f.centroid < c.minCentroidHz) continue;
      if (c.maxCentroidHz !== undefined && f.centroid > c.maxCentroidHz) continue;
      if (c.maxDb !== undefined && f.db > c.maxDb) continue;
      if (c.maxF1Hz !== undefined && !(f.f1 > 0 && f.f1 < c.maxF1Hz)) continue;
      if (c.maxTiltDb !== undefined && f.tiltDb > c.maxTiltDb) continue;
      return key;
    }

    if (f.voiced && f.f1 > 0 && f.f2 > 0) {
      let best = null, bd = Infinity;
      for (const k of this.vowelKeys) {
        const v = cfg.vowels[k];
        const d = Math.pow(Math.log(f.f1 / v.f1), 2) + Math.pow(Math.log(f.f2 / v.f2), 2);
        if (d < bd) { bd = d; best = k; }
      }
      return best;
    }
    return f.voiced ? 'RR' : 'sil';
  }

  // ---- plosive rescue: closure followed by a burst --------------------
  _markPlosives(q) {
    const P = this.cfg.plosives, hopMs = 1000 * this.cfg.audio.hopSamples / this.cfg.audio.sampleRate;
    const minClosure = Math.max(1, Math.round(P.closureMinMs / hopMs));
    const burstWin = Math.max(1, Math.round(P.burstWindowMs / hopMs));
    for (let i = 0; i < q.length; i++) {
      if (q[i].db > P.closureMaxDb) continue;
      let j = i; while (j < q.length && q[j].db <= P.closureMaxDb) j++;
      if (j - i < minClosure || j >= q.length) { i = j; continue; }
      let peak = -999, pc = 0;
      for (let k = j; k < Math.min(j + burstWin, q.length); k++)
        if (q[k].db > peak) { peak = q[k].db; pc = q[k].centroid; }
      if (peak - q[j - 1].db >= P.burstMinRiseDb) {
        let vis = 'PP';
        for (const rule of P.byCentroid) if (pc <= rule.maxHz) { vis = rule.viseme; break; }
        for (let k = j; k < Math.min(j + burstWin, q.length); k++) q[k].cls = vis;
      }
      i = j;
    }
  }

  _levelFor(db) {
    const I = this.cfg.intensity;
    const raw = Math.max(0, Math.min(1, (db - I.floorDb) / (I.ceilDb - I.floorDb)));
    const k = raw > this.intensity ? I.attackPerFrame : I.releasePerFrame;
    this.intensity += (raw - this.intensity) * k;
    return this.intensity;
  }

  _drain(force) {
    const cfg = this.cfg, T = cfg.timing;
    const hopMs = 1000 * cfg.audio.hopSamples / cfg.audio.sampleRate;
    const need = Math.ceil(T.lookaheadMs / hopMs);
    const out = [];
    while (this.pending.length > (force ? 0 : need)) {
      this._markPlosives(this.pending);
      const half = (T.medianFilterFrames - 1) >> 1;
      const win = this.pending.slice(0, Math.min(this.pending.length, half * 2 + 1)).map(f => f.cls);
      const counts = {};
      for (const c of win) counts[c] = (counts[c] || 0) + 1;
      let cls = win[0], bc = 0;
      for (const c of win) if (counts[c] > bc || (counts[c] === bc && c === win[0])) { bc = counts[c]; cls = c; }

      const f = this.pending.shift();
      const inten = this._levelFor(f.db);
      const heldFor = f.t - this.emittedAt;
      const holdOk = heldFor * 1000 >= T.minHoldMs;
      if (cls !== this.emitted && (holdOk || this.emitted === null)) {
        this.emitted = cls; this.emittedAt = f.t;
        out.push({
          t: +f.t.toFixed(4), viseme: cls,
          intensity: +inten.toFixed(3),
          level: Math.min(cfg.intensity.levels, 1 + Math.floor(inten * cfg.intensity.levels)),
          f1: Math.round(f.f1), f2: Math.round(f.f2), db: +f.db.toFixed(1)
        });
      }
    }
    return out;
  }

  push(samples) {
    const { hopSamples } = this.cfg.audio, N = this.N;
    const need = this.buf.length + samples.length;
    if (!this._merge || this._merge.length < need) this._merge = new Float32Array(Math.max(need, N * 4));
    const merged = this._merge.subarray(0, need);
    merged.set(this.buf); merged.set(samples, this.buf.length);
    let off = 0;
    while (off + N <= merged.length) {
      const t = (this.samplesConsumed + off) / this.cfg.audio.sampleRate;
      const f = this._analyse(merged.subarray(off, off + N), t);
      f.cls = this._classify(f);
      this.pending.push(f);
      off += hopSamples;
    }
    this.samplesConsumed += off;
    this.buf = Float32Array.prototype.slice.call(merged, off);
    return this._drain(false);
  }

  flush() { return this._drain(true); }
}

export default VisemeEngine;
